import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DoDisturbAltIcon from '@mui/icons-material/DoDisturbAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TableViewIcon from '@mui/icons-material/TableView';
import CalculateIcon from '@mui/icons-material/Calculate';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LogoutIcon from '@mui/icons-material/Logout';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SettingsIcon from '@mui/icons-material/Settings';
import BlockIcon from '@mui/icons-material/Block';
import PaymentsIcon from '@mui/icons-material/Payments';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';
import Logo from './Logo';

const drawerWidth = 280;

/**
 * Single source of truth for the whole navigation surface. The desktop
 * mega-menu bar AND the mobile drawer both render from this config, so they
 * cannot drift apart.
 *
 * `roles` (on a group or an item) hides the entry unless hasRole(...roles)
 * passes — cosmetic only; RequireRole + the server enforce access for real.
 * `match` decides active-state highlighting (prefix-aware so detail pages
 * like /results/:id highlight their section).
 */
const NAV_GROUPS = [
  {
    id: 'payments',
    title: 'Payments',
    roles: ['operations', 'finance'],
    items: [
      {
        title: 'Payment Batches',
        description: 'Weekly vendor batches — review, edit, and submit',
        path: '/payments/batches',
        icon: <PaymentsIcon />,
        match: (p) => p.startsWith('/payments/batches'),
      },
      {
        title: 'Upload New Batch',
        description: 'Import a weekly vendor payment file',
        path: '/payments/upload',
        icon: <CloudUploadIcon />,
        roles: ['operations'],
        match: (p) => p === '/payments/upload',
      },
      {
        title: 'Finance Review',
        description: 'Approve, adjust, and close submitted batches',
        path: '/payments/finance',
        icon: <FactCheckIcon />,
        roles: ['finance'],
        match: (p) => p === '/payments/finance',
      },
    ],
  },
  {
    id: 'calculator',
    title: 'Calculator',
    items: [
      {
        title: 'Upload Completed Work',
        description: 'Legacy weekly completed-work calculation',
        path: '/upload-data',
        icon: <CheckCircleIcon />,
        match: (p) => p === '/upload-data' || p.startsWith('/calculate'),
      },
      {
        title: 'Upload Refusal Work',
        description: 'Legacy refusal / unable-to-complete flow',
        path: '/upload-refusal',
        icon: <DoDisturbAltIcon />,
        match: (p) => p === '/upload-refusal',
      },
      {
        title: 'Results',
        description: 'Calculated statements and contractor emails',
        path: '/results',
        icon: <CalculateIcon />,
        match: (p) => p.startsWith('/results'),
      },
      {
        title: 'Projections',
        description: 'Forecast scenarios and estimates',
        path: '/projections',
        icon: <AssessmentIcon />,
        match: (p) => p.startsWith('/projections'),
      },
    ],
  },
  {
    id: 'setup',
    title: 'Setup',
    items: [
      {
        title: 'Rate Table',
        description: 'Providers and their negotiated rates',
        path: '/rate-table',
        icon: <TableViewIcon />,
        match: (p) => p === '/rate-table' || p.startsWith('/provider/edit') || p.startsWith('/provider/rates'),
      },
      {
        title: 'Add New Provider',
        description: 'Create a provider record with rates and emails',
        path: '/provider/add',
        icon: <PersonAddIcon />,
        match: (p) => p === '/provider/add',
      },
      {
        title: 'Service Types',
        description: 'Service catalog and baseline rates',
        path: '/service-types',
        icon: <SettingsIcon />,
        match: (p) => p === '/service-types',
      },
      {
        title: 'Non-Payment Orders',
        description: 'Order numbers excluded from payment',
        path: '/non-payment-orders',
        icon: <BlockIcon />,
        match: (p) => p === '/non-payment-orders',
      },
    ],
  },
];

const ACTIVE_BG = 'rgba(255, 255, 255, 0.15)';

/** Filter groups/items down to what the current user's roles allow. */
function visibleGroups(hasRole) {
  const allowed = (roles) =>
    !roles || (typeof hasRole === 'function' && hasRole(...roles));
  return NAV_GROUPS.filter((g) => allowed(g.roles))
    .map((g) => ({ ...g, items: g.items.filter((i) => allowed(i.roles)) }))
    .filter((g) => g.items.length > 0);
}

function groupIsActive(group, pathname) {
  return group.items.some((i) => i.match(pathname));
}

/** One desktop nav group: a trigger button opening a rich panel. */
function NavGroupMenu({ group, pathname }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const active = groupIsActive(group, pathname);

  return (
    <Box>
      <Button
        id={`nav-${group.id}-button`}
        aria-controls={open ? `nav-${group.id}-menu` : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        endIcon={<KeyboardArrowDownIcon />}
        color="inherit"
        sx={{ mx: 0.5, backgroundColor: active ? ACTIVE_BG : 'transparent' }}
      >
        {group.title}
      </Button>
      <Menu
        id={`nav-${group.id}-menu`}
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{ 'aria-labelledby': `nav-${group.id}-button`, sx: { py: 1 } }}
        slotProps={{ paper: { sx: { minWidth: 340, mt: 0.5 } } }}
      >
        {group.items.map((item) => (
          <MenuItem
            key={item.path}
            component={Link}
            to={item.path}
            onClick={() => setAnchorEl(null)}
            selected={item.match(pathname)}
            sx={{ alignItems: 'flex-start', py: 1.25 }}
          >
            <ListItemIcon sx={{ mt: 0.5 }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.title}
              secondary={item.description}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

/** Avatar + account menu: identity, role chips, logout. */
function UserMenu({ user, roles, onLogout }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const initials = (user?.email || '?').slice(0, 2).toUpperCase();

  return (
    <Box sx={{ ml: 1.5 }}>
      <Tooltip title={user?.email || 'Account'}>
        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          size="small"
          aria-label="account menu"
          aria-controls={open ? 'account-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <Avatar sx={{ width: 36, height: 36, bgcolor: 'secondary.main', fontSize: 15 }}>
            {initials}
          </Avatar>
        </IconButton>
      </Tooltip>
      <Menu
        id="account-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { sx: { minWidth: 240, mt: 0.5 } } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="body2" noWrap>{user?.email}</Typography>
          {roles.length > 0 && (
            <Box sx={{ mt: 0.75, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {roles.map((r) => (
                <Chip key={r} label={r.replace('_', ' ')} size="small" variant="outlined" />
              ))}
            </Box>
          )}
        </Box>
        <Divider />
        <MenuItem onClick={onLogout}>
          <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </Box>
  );
}

function Navigation() {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const { user, roles = [], signOut, hasRole } = useAuth();

  const groups = visibleGroups(hasRole);
  const dashboardActive = location.pathname === '/';

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleLogout = async () => {
    await signOut();
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'left' }}>
      <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
        <Logo height={60} showText={false} />
      </Box>
      <Divider />
      <List dense>
        <ListItem disablePadding>
          <ListItemButton component={Link} to="/" selected={dashboardActive}>
            <ListItemIcon><DashboardIcon /></ListItemIcon>
            <ListItemText primary="Dashboard" />
          </ListItemButton>
        </ListItem>
        {groups.map((group) => (
          <Box key={group.id}>
            <ListSubheader component="div" sx={{ lineHeight: '36px', bgcolor: 'transparent' }}>
              {group.title.toUpperCase()}
            </ListSubheader>
            {group.items.map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  component={Link}
                  to={item.path}
                  selected={item.match(location.pathname)}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.title} />
                </ListItemButton>
              </ListItem>
            ))}
          </Box>
        ))}
        {user && (
          <>
            <Divider sx={{ my: 1 }} />
            <ListItem disablePadding>
              <ListItemButton onClick={handleLogout}>
                <ListItemIcon><LogoutIcon /></ListItemIcon>
                <ListItemText primary="Logout" secondary={user.email} />
              </ListItemButton>
            </ListItem>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar sx={{ minHeight: { xs: '72px', sm: '80px' } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'block' } }}>
            <Logo height={64} />
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
            <Button
              component={Link}
              to="/"
              color="inherit"
              startIcon={<DashboardIcon />}
              sx={{ mx: 0.5, backgroundColor: dashboardActive ? ACTIVE_BG : 'transparent' }}
            >
              Dashboard
            </Button>
            {groups.map((group) => (
              <NavGroupMenu key={group.id} group={group} pathname={location.pathname} />
            ))}
            {user && <UserMenu user={user} roles={roles} onLogout={handleLogout} />}
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="temporary"
        open={isMobile && mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
}

export default Navigation;
